import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase/client';
import { toast } from 'sonner';
import { useSupabaseAuth } from '../auth/useSupabaseAuth';

export interface WorkingHoursDay {
  id?: string;
  day_of_week: string;
  start_time: string | null;
  end_time: string | null;
  is_closed: boolean;
}

interface WorkingHoursMap {
  [key: string]: WorkingHoursDay;
}

export function useWorkingHours(businessId: number | null) {
  const [workingHours, setWorkingHours] = useState<WorkingHoursMap>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [saving, setSaving] = useState(false);
  const { isSignedIn } = useSupabaseAuth();

  const loadWorkingHours = useCallback(async () => {
    if (!businessId || !isSignedIn) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('Loading working hours for business:', businessId);
      
      const { data, error: fetchError } = await supabase
        .from('working_hours')
        .select('*')
        .eq('user_id', businessId)
        .order('day_of_week');

      if (fetchError) throw fetchError;

      // Convert array to map for easier access
      const hoursMap: WorkingHoursMap = {};
      
      // Initialize with default hours for all days
      const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      days.forEach((day) => {
        hoursMap[day] = {
          day_of_week: day,
          start_time: day === 'sunday' ? null : '09:00',
          end_time: day === 'sunday' ? null : '17:00',
          is_closed: day === 'sunday'
        };
      });

      // Override with actual data from database
      if (data && data.length > 0) {
        data.forEach((item) => {
          hoursMap[item.day_of_week] = {
            id: item.id,
            day_of_week: item.day_of_week,
            start_time: item.start_time,
            end_time: item.end_time,
            is_closed: item.is_closed || false
          };
        });
      }

      setWorkingHours(hoursMap);
      setError(null);
    } catch (err) {
      console.error('Error loading working hours:', err);
      setError(err instanceof Error ? err : new Error('Failed to load working hours'));
      toast.error('Failed to load working hours');
    } finally {
      setLoading(false);
    }
  }, [businessId, isSignedIn]);

  const updateWorkingHours = async (day: string, updates: Partial<WorkingHoursDay>) => {
    if (!businessId || !isSignedIn) {
      throw new Error('Not authenticated or no business selected');
    }

    try {
      setSaving(true);
      
      const currentHours = workingHours[day];
      const updatedHours = { ...currentHours, ...updates };
      
      // If setting as closed, clear times
      if (updates.is_closed) {
        updatedHours.start_time = null;
        updatedHours.end_time = null;
      }
      
      // If opening time is set to "Closed", mark as closed
      if (updates.start_time === 'Closed') {
        updatedHours.is_closed = true;
        updatedHours.start_time = null;
        updatedHours.end_time = null;
      }

      // Prepare data for upsert
      const upsertData = {
        user_id: businessId,
        day_of_week: day,
        start_time: updatedHours.start_time,
        end_time: updatedHours.end_time,
        is_closed: updatedHours.is_closed,
        updated_at: new Date().toISOString()
      };

      console.log('Upserting working hours:', upsertData);

      const { data, error: upsertError } = await supabase
        .from('working_hours')
        .upsert(upsertData, {
          onConflict: 'user_id,day_of_week'
        })
        .select()
        .single();

      if (upsertError) throw upsertError;

      // Update local state
      setWorkingHours(prev => ({
        ...prev,
        [day]: {
          ...updatedHours,
          id: data?.id || updatedHours.id
        }
      }));

      toast.success(`Updated ${day}'s hours`);
      return updatedHours;
    } catch (err) {
      console.error('Error updating working hours:', err);
      const message = err instanceof Error ? err.message : 'Failed to update working hours';
      toast.error(message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const saveAllWorkingHours = async () => {
    if (!businessId || !isSignedIn) {
      throw new Error('Not authenticated or no business selected');
    }

    try {
      setSaving(true);
      
      // Prepare all days for batch upsert
      const upsertData = Object.entries(workingHours).map(([day, hours]) => ({
        user_id: businessId,
        day_of_week: day,
        start_time: hours.start_time,
        end_time: hours.end_time,
        is_closed: hours.is_closed,
        updated_at: new Date().toISOString()
      }));

      console.log('Batch upserting working hours:', upsertData);

      const { error: upsertError } = await supabase
        .from('working_hours')
        .upsert(upsertData, {
          onConflict: 'user_id,day_of_week'
        });

      if (upsertError) throw upsertError;

      toast.success('Working hours saved successfully');
    } catch (err) {
      console.error('Error saving all working hours:', err);
      const message = err instanceof Error ? err.message : 'Failed to save working hours';
      toast.error(message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  // Load working hours when business ID changes
  useEffect(() => {
    loadWorkingHours();
  }, [loadWorkingHours]);

  return {
    workingHours,
    loading,
    error,
    saving,
    updateWorkingHours,
    saveAllWorkingHours,
    reloadWorkingHours: loadWorkingHours
  };
}